// @ts-nocheck
import type { ItemDefinition } from '../../../../../shims/appStub';
import type { DataRef, ValueOrExpression } from '../../types/binding';
import type { ExpressionDefinition, FieldExpressionBinding } from '../../types/canonical';
import type { PropertyDefinition } from '../../types/category';
import type { BindingContext } from '../binding/types';
import { resolveRef } from '../binding/BindingResolver';
import { evaluateExpressionById } from '../expressions';
import type { MutationRecord } from '../action/types';
import { normalizeMutationItemId } from '../binding/itemFieldPolicy';
import { DEFAULT_COERCION_POLICY, writePropertyValue } from '../bridge';
import type { ArbitraryFieldEntry } from '../bridge';

const ITEM_COLOR_FIELDS = new Set(['backgroundColor', 'textColor', 'borderColor', 'primaryColor', 'secondaryColor']);
const TRANSPARENT_COLOR = '#00000000';

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a == null && b == null) {
    return true;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Empty color strings from expressions mean "no color"; use a stable transparent hex. */
function normalizeItemColorBindingValue(
  field: string,
  resolved: unknown,
): unknown {
  if (!ITEM_COLOR_FIELDS.has(field)) {
    return resolved;
  }
  if (resolved === '' || resolved == null) {
    return TRANSPARENT_COLOR;
  }
  return resolved;
}

function resolveBindingValue(
  value: ValueOrExpression<unknown>,
  context: BindingContext,
  expressions: readonly ExpressionDefinition[],
): unknown {
  if (value.kind === 'static') {
    return value.value;
  }
  const result = evaluateExpressionById(value.expressionId, [...expressions], context, {
    profile: 'production',
  });
  return result.ok ? result.value : undefined;
}

function readTargetValue(ref: DataRef, item: ItemDefinition, context: BindingContext): unknown {
  if (ref.kind === 'itemField') {
    return (item as Record<string, unknown>)[ref.field];
  }
  if (ref.kind === 'categoryProperty') {
    return resolveRef(ref, { ...context, item }).value;
  }
  return undefined;
}

export interface FieldBindingEvaluationResult {
  workingItem: ItemDefinition;
  mutations: MutationRecord[];
}

export function evaluateFieldBindings(
  bindings: readonly FieldExpressionBinding[],
  snapshotItem: ItemDefinition,
  context: BindingContext,
  expressions: readonly ExpressionDefinition[],
): FieldBindingEvaluationResult {
  if (bindings.length === 0) {
    return { workingItem: snapshotItem, mutations: [] };
  }

  let workingItem: ItemDefinition = {
    ...snapshotItem,
    arbitraryFieldsList: snapshotItem.arbitraryFieldsList
      ? snapshotItem.arbitraryFieldsList.map((f) => ({ ...f }))
      : [],
    imageUris: [...(snapshotItem.imageUris ?? [])],
    tagList: [...(snapshotItem.tagList ?? [])],
  };

  const readContext: BindingContext = { ...context, item: snapshotItem };
  const mutations: MutationRecord[] = [];

  for (const binding of bindings) {
    let resolved = resolveBindingValue(binding.value, readContext, expressions);
    if (resolved === undefined && binding.value.kind === 'expression') {
      continue;
    }
    if (binding.target.kind === 'itemField') {
      resolved = normalizeItemColorBindingValue(binding.target.field, resolved);
    }

    const current = readTargetValue(binding.target, snapshotItem, readContext);
    if (valuesEqual(current, resolved)) {
      continue;
    }

    if (binding.target.kind === 'itemField') {
      workingItem = {
        ...workingItem,
        [binding.target.field]: resolved,
      } as ItemDefinition;
      mutations.push({
        kind: 'itemField',
        itemId: normalizeMutationItemId(snapshotItem.id),
        field: binding.target.field,
        value: resolved,
      });
      if (
        binding.target.field === 'dimensionsHeight'
        && typeof resolved === 'number'
        && Number.isFinite(resolved)
      ) {
        workingItem = queueCanvasBoxMutation(
          snapshotItem,
          'height',
          resolved,
          workingItem,
          mutations,
        );
      }
    } else if (binding.target.kind === 'categoryProperty') {
      const target = binding.target;
      const category = context.compiledTemplate.categoriesById[target.categoryId];
      const property = category?.properties.find((p) => p.id === target.propertyId) as
        | PropertyDefinition
        | undefined;
      if (!property) {
        continue;
      }
      const fields: ArbitraryFieldEntry[] = (workingItem.arbitraryFieldsList ?? []).map((f) => ({
        categoryId: f.categoryId,
        propertyId: f.propertyId,
        value: f.value,
      }));
      const writeResult = writePropertyValue(
        target.categoryId,
        target.propertyId,
        resolved,
        fields,
        property,
        DEFAULT_COERCION_POLICY,
      );
      if (writeResult.success && writeResult.updatedFields) {
        workingItem = {
          ...workingItem,
          arbitraryFieldsList: writeResult.updatedFields.map((f) => ({
            categoryId: f.categoryId,
            propertyId: f.propertyId,
            value: String(f.value),
          })),
        };
        const serialized =
          resolved === null || resolved === undefined ? '' : String(resolved);
        mutations.push({
          kind: 'categoryProperty',
          itemId: normalizeMutationItemId(snapshotItem.id),
          categoryId: target.categoryId,
          propertyId: target.propertyId,
          value: serialized,
        });
      }
    }
  }

  if (
    typeof workingItem.dimensionsHeight === 'number'
    && Number.isFinite(workingItem.dimensionsHeight)
  ) {
    workingItem = queueCanvasBoxMutation(
      snapshotItem,
      'height',
      workingItem.dimensionsHeight,
      workingItem,
      mutations,
    );
  }

  return { workingItem, mutations };
}

const CANVAS_BOX_SIZE_FIELDS = new Set(['height', 'width']);

function queueCanvasBoxMutation(
  snapshotItem: ItemDefinition,
  field: string,
  value: unknown,
  workingItem: ItemDefinition,
  mutations: MutationRecord[],
): ItemDefinition {
  if (!CANVAS_BOX_SIZE_FIELDS.has(field)) {
    return workingItem;
  }
  const current = (snapshotItem as Record<string, unknown>)[field];
  if (valuesEqual(current, value)) {
    return workingItem;
  }
  const next = {
    ...workingItem,
    [field]: value,
  } as ItemDefinition;
  mutations.push({
    kind: 'itemField',
    itemId: normalizeMutationItemId(snapshotItem.id),
    field,
    value,
  });
  return next;
}
